import { defineComponent as _defineComponent } from 'vue'
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faBringFront } from '@fortawesome/pro-regular-svg-icons';
import { GeoObject, GeoObjectBaseGeometry, GeoObjectType } from 'farmdok-rest-api';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';

import i18n from '@/initI18n';

import { Getters as AuthGetters } from '@/auth/store/getters';
import CheckDistanceSidebar from '@/fields/components/CheckDistanceSidebar.vue';
import { Getters as FieldGetters } from '@/fields/store/getters';
import TheContentbar from '@/layout/components/TheContentbar.vue';
import ThePageSkeleton from '@/layout/components/ThePageSkeleton.vue';
import SidebarCreateReport from '@/map/components/SidebarCreateReport.vue';
import Orientation from '@/map/types';
import { Getters as ProductGetters } from '@/products/store/getters';
import { Field, Product } from '@/shared/api/rest/models';
import CompanySwitch from '@/shared/components/CompanySwitch.vue';
import DropdownPageOptions from '@/shared/components/DropdownPageOptions.vue';
import FeatureNotAvailable from '@/shared/components/FeatureNotAvailable.vue';
import ProcessOrderSwitch from '@/shared/components/ProcessOrderSwitch.vue';
import Button from '@/shared/components/buttons/Button.vue';
import ButtonDropdown from '@/shared/components/buttons/ButtonDropdown.vue';
import { availableFeatures } from '@/shared/storeDynamicFeatures';
import { useStore } from '@/store';

import CreateFieldsSidebar from './components/CreateFieldsSidebar.vue';
import EditFieldsSidebar from './components/EditFieldsSidebar.vue';
import MapObjectsSidebar from './components/MapObjectsSidebar.vue';
import SidebarFields from './components/SidebarFields.vue';
import FieldsMapContainer from './containers/FieldsMapContainer.vue';
import { MenuItemId, SelectionSource } from './types';


export default /*#__PURE__*/_defineComponent({
  __name: 'PageFieldsMap',
  setup(__props) {

library.add(faSpinnerThird);
library.add(faBringFront);

const t = i18n.translate;
const store = useStore();
const router = useRouter();

/** TEMPLATE REFS */
const sidebarFields = ref<InstanceType<typeof SidebarFields> | null>(null);
const fieldsMapContainer = ref<InstanceType<typeof FieldsMapContainer> | null>(null);
const editFieldsSidebar = ref<InstanceType<typeof EditFieldsSidebar> | null>(null);
const mapObjectsSidebar = ref<InstanceType<typeof MapObjectsSidebar> | null>(null);

/** DATA REFS */
const selectedFields = ref<string[]>([]);
const visibleFields = ref<string[]>([]);
const buttonUpdateSelection = ref([
  {
    text: t('Auswahl aufheben'),
    handler: () => (sidebarFields.value as any).$refs.table.$refs.table.selectableRowsBatchAction('clear'),
  },
  {
    text: t('Auswahl umkehren'),
    handler: () => (sidebarFields.value as any).$refs.table.$refs.table.selectableRowsBatchAction('reverse'),
  },
  {
    text: t('Alle auswählen'),
    handler: () => (sidebarFields.value as any).$refs.table.$refs.table.selectableRowsBatchAction('all'),
  },
]);
const createFieldsActive = ref(false);
const editFieldsActive = ref(false);
const currentMapObjectId = ref<string | null>(null);
const currentFieldId = ref<string | null>(null);
const newFields = ref<Field[]>([]);
const geoDataChanges = ref({});
const invalidNewFields = ref<Field[]>([]);
const invalidEditFields = ref<Field[]>([]);
const errorMessage = ref<string | null>(null);
const createFieldsCurrentStep = ref(0);
const companyId = ref<string | null>(null);
const missingCompany = ref(false);
const missingName = ref(false);
const uniqueNameViolation = ref(false);
const currentMapObjectGeometry = ref<GeoObjectBaseGeometry | null>(null);
const createReportActive = ref(false);
const selectionSource = ref('MAP');
const checkDistanceActive = ref<boolean>(false);
const checkDistance = ref<number>(200);

onMounted(() => {
  store.dispatch('fields/subscribe');
  fetchGeoObjects();
});

const createMapObjectActive = computed(() => store.state.geoObjects.isCreateActive);
const editMapObjectActive = computed(() => store.state.geoObjects.isEditActive);
const sidebarFieldsVisible = computed(
  () =>
    !createFieldsActive.value &&
    !editFieldsActive.value &&
    !createMapObjectActive.value &&
    !editMapObjectActive.value &&
    !createReportActive.value &&
    !checkDistanceActive.value,
);
const currentMapObjectType = computed(() => store.state.geoObjects.currentObjectType);
const currentMapObject = computed(() => {
  if (currentMapObjectId.value) {
    return store.getters['geoObjects/getById'](currentMapObjectId.value);
  }
  return null;
});
const featureGISObjectsEnabled = computed<boolean>(
  () =>
    store.getters.currentCompaniesHaveFeatureEnabled(availableFeatures.FEATURE_MAP_GEO_OBJECTS) &&
    store.getters.currentCompaniesHaveFeatureVisible(availableFeatures.FEATURE_MAP_GEO_OBJECTS),
);
const fields = computed(() => store.state.fields.data);
const currentCompaniesHaveFeature = computed<{ enabled: boolean; readonly: boolean; visible: boolean }>(() =>
  store.getters.currentCompaniesHaveFeature(availableFeatures.FEATURE_FIELDS),
);
const fieldsReadOnly = computed<boolean>(() =>
  store.getters.currentCompaniesHaveFeatureReadonly(availableFeatures.FEATURE_FIELDS),
);
const loading = computed<boolean>(() => store.getters['fields/loading']);
const editMode = computed<boolean>(
  () => !loading.value && Object.keys(fields.value).length > 0 && selectedFields.value.length > 0,
);
const companies = computed(() => store.state.auth.currentCompanies);
const processOrderName = computed<string | null>(() => store.state.auth.currentProcessOrderName);
const mapFeatureEnabled = computed<boolean>(
  () =>
    store.getters.currentCompaniesHaveFeatureEnabled(availableFeatures.FEATURE_MAP) &&
    store.getters.currentCompaniesHaveFeatureVisible(availableFeatures.FEATURE_MAP),
);

const mapFeatureVisible = computed(() =>
  store.getters.currentCompaniesHaveFeatureVisible(availableFeatures.FEATURE_MAP),
);

const requireUniqueFieldName = computed<boolean>(() =>
  store.getters.currentCompaniesHaveFeatureEnabled(availableFeatures.FEATURE_FIELD_NAMES_UNIQUE),
);

const isolationZoneSubTypes = computed<string[]>(() =>
  store.getters['geoObjects/getIsolationZoneSubTypes'].map((s) => s.name),
);

function toggleCreateFieldsActive(active: boolean) {
  createFieldsActive.value = active;
  if (!active) {
    resetDirtyFormValues();
  }
}

function toggleEditFieldsActive(active: boolean) {
  editFieldsActive.value = active;
  if (!active) {
    resetDirtyFormValues();
  }
}

async function saveEditFields(fieldIds: string[], fieldChanges: any) {
  missingCompany.value = false;
  missingName.value = false;
  uniqueNameViolation.value = false;
  errorMessage.value = '';

  if (invalidEditFields.value.length > 0) {
    return;
  }

  const changes = { ...fieldChanges };

  if (changes.name && requireUniqueFieldName.value) {
    if (fieldIds.length > 1) {
      uniqueNameViolation.value = true;
      errorMessage.value = t('Felder dürfen nicht die gleichen Namen enthalten.') ?? '';
      return;
    }

    const isNameViolation = (store.getters['fields/isUniqueFieldName'] as FieldGetters['isUniqueFieldName'])(
      changes.name,
    );
    if (isNameViolation) {
      uniqueNameViolation.value = true;
      errorMessage.value = t('Ein Feld mit diesem Namen existiert bereits.') ?? '';
      return;
    }
  }

  fieldIds.forEach(async (fieldId) => {
    if (Object.values(changes).length === 0 && (geoDataChanges.value as any)[fieldId] == null) {
      return;
    }

    if (changes.cropId) {
      const field = (store.getters['fields/getById'] as FieldGetters['getById'])(fieldId);
      const company = (store.getters['auth/getCompanyByProcessOrderId'] as AuthGetters['getCompanyByProcessOrderId'])(
        field.processOrderId,
      );

      const crop = (store.getters['products/getProductOrSibling'] as ProductGetters['getProductOrSibling'])(
        changes.cropId,
        company.id,
      ) as Product;
      changes.cropId = crop.id;
    }

    const entry = {
      ...changes,
      ...((geoDataChanges.value as any)[fieldId] != null ? (geoDataChanges.value as any)[fieldId] : {}),
      id: fieldId,
    };
    if (changes['fieldGroup.customerId'] === 'own') {
      entry['fieldGroup.customerId'] = null;
    }
    await store.dispatch('fields/updateAndSyncEntry', { entry });
    resetDirtyFormValues();
  });
}

function onUpdateMapObject(entry: { entry: GeoObject }) {
  store.dispatch('geoObjects/updateAndSyncEntry', entry);
}

function onSaveMapObject(entry: { entry: GeoObject }) {
  store.dispatch('geoObjects/insertAndSyncNewEntry', entry);
}

function onDeleteMapObject(entry: { entry: GeoObject }) {
  store.dispatch('geoObjects/removeAndSyncEntry', entry);
}

async function onMapObjectSelected(mapObjectId: string) {
  const geoObject = (await store.getters['geoObjects/getById'](mapObjectId)) as GeoObject;
  store.dispatch('geoObjects/setCurrentObjectType', geoObject.objectType);
  currentMapObjectId.value = mapObjectId;
  store.dispatch('geoObjects/setIsEditActive', true);
}

async function handleMenuAction(itemId: string) {
  if (itemId === MenuItemId.CREATE_REPORT) {
    createReportActive.value = true;
    return;
  }

  store.dispatch('geoObjects/toggleIsCreateActive', true);
  store.dispatch('geoObjects/setCurrentObjectType', getMapObjectType(itemId));
}

function updateMapObjectPolyline(line: any) {
  currentMapObjectGeometry.value = line;
}

function onCancelMapObjects() {
  currentMapObjectId.value = null;
  store.dispatch('geoObjects/setIsCreateActive', false);
  store.dispatch('geoObjects/setIsEditActive', false);

  (fieldsMapContainer.value as any).$refs.fieldsMap.drawMapObjects(true);
}

function getVisibleColumns() {
  return (sidebarFields.value as any).$refs.table.$refs.table.getVisibleColumns();
}

function getMapObjectType(itemId: string) {
  switch (itemId) {
    case MenuItemId.ISOLATION_ZONES:
      return GeoObjectType.IsolationZone;
    case MenuItemId.MULTIPLIER_GROUPS:
      return GeoObjectType.MultiplierGroup;
    default:
      throw new Error('Unknown menu item type');
  }
}

function saveNewFields() {
  missingCompany.value = false;
  missingName.value = false;
  uniqueNameViolation.value = false;
  errorMessage.value = '';

  const usePlaceholderName = createFieldsCurrentStep.value === 0;

  if (companies.value.length > 1 && companyId.value == null) {
    missingCompany.value = true;
  }

  const missingNameNew = newFields.value.some((field: Field) => {
    if (typeof field.name === 'string' && field.name.length > 0) {
      return false;
    }
    updateCurrentFieldId(field.id);
    return true;
  });

  if (missingNameNew && !usePlaceholderName) {
    missingName.value = true;
  }

  if (missingName.value || missingCompany.value) {
    return;
  }

  if (requireUniqueFieldName.value) {
    const isNameViolation = newFields.value.some((field: Field) => {
      if ((store.getters['fields/isUniqueFieldName'] as FieldGetters['isUniqueFieldName'])(field.name)) {
        updateCurrentFieldId(field.id);
        return true;
      }
      return false;
    });

    if (isNameViolation) {
      uniqueNameViolation.value = true;
      errorMessage.value = t('Ein Feld mit diesem Namen existiert bereits.') ?? '';
      return;
    }
  }

  newFields.value.forEach((field: Field) => {
    const entry: Field = {
      ...field,
    };
    if (companies.value.length > 1) {
      (entry as any)['processOrder.companyId'] = companyId.value;
    }
    if (usePlaceholderName) {
      entry.name = field.placeholderName;
    }
    store.dispatch('fields/insertAndSyncNewEntry', { entry });
  });

  resetDirtyFormValues();
}

function restoreFields() {
  router.push({ name: 'fields/restore' });
}

function onUpdateVisibleFields(visible: string[]) {
  visibleFields.value = visible;
}

function onUpdateSelectedFields(selected: string[], source: SelectionSource): void {
  selectionSource.value = source;
  selectedFields.value = selected;
}

function addFieldsButtonPressed() {
  createFieldsActive.value = true;
}

function updateNewFields(updatedFields: Field[]) {
  newFields.value = updatedFields;
  errorMessage.value = '';
}

function updateCurrentFieldId(current: string) {
  currentFieldId.value = current;
}

function updateSelectedCompanyId(id: string) {
  companyId.value = id;
  if (id) {
    missingCompany.value = false;
  }
}

async function resetDirtyFormValues() {
  selectedFields.value = [];
  newFields.value = [];
  geoDataChanges.value = {};
  currentFieldId.value = null;
  companyId.value = null;
  createFieldsActive.value = false;
  editFieldsActive.value = false;
  invalidEditFields.value = [];
  invalidNewFields.value = [];
  errorMessage.value = '';
}

function splitFieldLineValid(valid: Boolean) {
  if (valid === undefined || valid) {
    errorMessage.value = '';
  } else {
    errorMessage.value =
      t(
        'Die aktuelle Trennlinie ist ungültig. Zeichne eine Linie die das aktuelle Feld in genau zwei Teile teilt. Du kannst die Trennlinie durch Ziehen der Eckpunkte anpassen.',
      ) ?? '';
  }
}

function edit() {
  editFieldsActive.value = true;

  if (selectedFields.value.length === 1) {
    [currentFieldId.value] = selectedFields.value;
  }
}

function fetchGeoObjects() {
  store.dispatch('geoObjects/subscribe');
}

async function exportReport(options: { legendTitle: string; orientation: Orientation }) {
  await (fieldsMapContainer.value as any).$refs.fieldsMap.printMap(options);
}

watch(companies, () => fetchGeoObjects());
watch(processOrderName, () => fetchGeoObjects());

return { __sfc: true,t, store, router, sidebarFields, fieldsMapContainer, editFieldsSidebar, mapObjectsSidebar, selectedFields, visibleFields, buttonUpdateSelection, createFieldsActive, editFieldsActive, currentMapObjectId, currentFieldId, newFields, geoDataChanges, invalidNewFields, invalidEditFields, errorMessage, createFieldsCurrentStep, companyId, missingCompany, missingName, uniqueNameViolation, currentMapObjectGeometry, createReportActive, selectionSource, checkDistanceActive, checkDistance, createMapObjectActive, editMapObjectActive, sidebarFieldsVisible, currentMapObjectType, currentMapObject, featureGISObjectsEnabled, fields, currentCompaniesHaveFeature, fieldsReadOnly, loading, editMode, companies, processOrderName, mapFeatureEnabled, mapFeatureVisible, requireUniqueFieldName, isolationZoneSubTypes, toggleCreateFieldsActive, toggleEditFieldsActive, saveEditFields, onUpdateMapObject, onSaveMapObject, onDeleteMapObject, onMapObjectSelected, handleMenuAction, updateMapObjectPolyline, onCancelMapObjects, getVisibleColumns, getMapObjectType, saveNewFields, restoreFields, onUpdateVisibleFields, onUpdateSelectedFields, addFieldsButtonPressed, updateNewFields, updateCurrentFieldId, updateSelectedCompanyId, resetDirtyFormValues, splitFieldLineValid, edit, fetchGeoObjects, exportReport, CheckDistanceSidebar, TheContentbar, ThePageSkeleton, SidebarCreateReport, CompanySwitch, DropdownPageOptions, FeatureNotAvailable, ProcessOrderSwitch, Button, ButtonDropdown, CreateFieldsSidebar, EditFieldsSidebar, MapObjectsSidebar, SidebarFields, FieldsMapContainer }
}

})